import { useState } from 'react'
import {
  Sparkles, Search, X, CheckCircle2, ShieldAlert, Eye, Layers, Plus,
  Clock, AlertTriangle, CircleSlash, History, ChevronLeft, ChevronRight, HandCoins,
  Trash2, Zap, User,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import {
  useParlayLabAllowance, useParlayLabFixtureSearch, useParlayLabFamilies, useGenerateParlayLab,
  useParlayLabHistory, useParlayLabFixturePicks, useEvaluateParlayLab,
  getParlayLabErrorMessage,
} from '@/hooks/useParlayLab'
import type {
  ParlayLabFixtureSummary, ParlayLabFamily, ParlayLabRiskProfile, ParlayLabResult,
  ParlayLabPick, EvaluateLegInput,
} from '@/hooks/useParlayLab'
import { TeamCrest } from '@/components/TeamCrest'
import { UpgradePrompt } from '@/components/UpgradePrompt'
import { EmptyState } from '@/components/EmptyState'
import { ResponsibleNotice } from '@/components/ResponsibleNotice'
import { RegisterBetModal } from '@/components/RegisterBetModal'
import { PlayedBadge } from '@/components/PlayedBadge'
import { formatOdds, formatProb, cn, formatKickoff, formatKickoffTime } from '@/lib/utils'

const RISK_PROFILES: { value: ParlayLabRiskProfile; label: string; hint: string }[] = [
  { value: 'conservador', label: 'Conservador', hint: 'Prioriza estabilidad y probabilidad.' },
  { value: 'balanceado', label: 'Balanceado', hint: 'Equilibrio real entre probabilidad, calidad y cuota.' },
  { value: 'agresivo', label: 'Agresivo', hint: 'Admite más cuota buscando upside, sin sacrificar calidad.' },
]

const FAMILY_LABELS: Record<string, string> = {
  resultado: 'Resultado', goles: 'Goles', corners: 'Córners', tarjetas: 'Tarjetas', tiros: 'Tiros', jugadores: 'Jugadores',
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  valid: { label: 'Válido', className: 'text-emerald-400 border-emerald-500/40 bg-emerald-950/30' },
  no_parlay: { label: 'Sin parlay', className: 'text-text-dim border-border bg-panel-elevated' },
  target_not_achievable: { label: 'Objetivo no alcanzado', className: 'text-warn border-warn/40 bg-warn-soft/20' },
  informacion_insuficiente: { label: 'Información insuficiente', className: 'text-text-dim border-border bg-panel-elevated' },
}

function correlationRiskLabel(level: string | null | undefined): string {
  if (level === 'media') return 'Riesgo de correlación medio'
  if (level === 'alta') return 'Riesgo de correlación alto'
  return 'Riesgo de correlación bajo'
}

// P1.2 (01/10/2026, Timezone Global -- Requisito B): antes usaban `toLocaleString()` sin
// `timeZone` explícito (hora del DISPOSITIVO, inconsistente con el resto de la app) --
// delegan al formatter central (`resolveDisplayTimeZone()`), mismos nombres/call-sites.
function dateLabel(iso?: string | null): string {
  return formatKickoff(iso, { withWeekday: false })
}

function kickoffLabel(iso?: string | null): string {
  return formatKickoff(iso, { withMonth: false })
}

function ReasonList({ reasons, emptyText }: { reasons: string[]; emptyText: string }) {
  if (!reasons || reasons.length === 0) {
    return <p className="text-xs text-text-dim italic leading-relaxed">{emptyText}</p>
  }
  return (
    <ul className="space-y-1.5">
      {reasons.map((r, i) => (
        <li key={i} className="text-xs text-text-muted leading-relaxed flex items-start gap-2">
          <span className="text-brand mt-1 shrink-0">•</span>
          <span>{r}</span>
        </li>
      ))}
    </ul>
  )
}

// ─── Shared: LegRow ────────────────────────────────────────────────────────

function LegRow({ leg, onRemove }: { leg: ParlayLabResult['legs'][number]; onRemove?: () => void }) {
  const bookmaker = leg.odds_source?.bookmaker
  const capturedText = leg.odds_source?.reference_text
  return (
    <div className="data-row items-start">
      <div className="flex items-center -space-x-1.5 shrink-0 mt-0.5">
        <TeamCrest src={leg.home_logo ?? null} alt={leg.home_team ?? ''} size={18} />
        <TeamCrest src={leg.away_logo ?? null} alt={leg.away_team ?? ''} size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-text truncate">{leg.selection_label ?? leg.selection}</p>
        <p className="text-[10px] text-text-dim truncate">
          {leg.home_team} vs {leg.away_team} · {leg.family_label ?? leg.family}
        </p>
        {(bookmaker || capturedText) && (
          <p className="text-[9px] text-text-dim/70 truncate mt-0.5">
            {bookmaker ? `Cuota observada en ${bookmaker}` : capturedText}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end shrink-0">
        <span className="figure text-sm font-bold text-text">{formatOdds(leg.odds)}</span>
        {leg.fair_odds != null && (
          <span className="figure text-[9px] text-text-dim">justa {formatOdds(leg.fair_odds)}</span>
        )}
      </div>
      {onRemove && (
        <button onClick={onRemove} className="ml-2 shrink-0 text-text-dim hover:text-danger transition" aria-label="Quitar selección">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

// ─── HOME SCREEN ───────────────────────────────────────────────────────────

type Mode = 'home' | 'mode_a' | 'mode_b' | 'history' | 'result'
type ModeBStep = 'search' | 'picks' | 'review' | 'analyzing' | 'result'

function HomeScreen({ onSelect }: { onSelect: (mode: 'mode_a' | 'mode_b') => void }) {
  return (
    <div className="space-y-6">
      {/* Hero -- mismo lenguaje visual que JA Lab (dark/neon-green premium), referencia aprobada */}
      <section className="relative overflow-hidden rounded-3xl border border-brand/25 bg-gradient-to-b from-brand-soft/15 via-panel to-panel px-6 py-8 md:px-8 md:py-10">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
        <div className="relative space-y-2.5">
          <span className="eyebrow text-brand inline-flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5" /> Parlay Lab
          </span>
          <h1 className="font-display text-xl md:text-3xl font-bold text-text leading-tight">
            Crea y analiza parlays con IA
          </h1>
          <p className="text-xs md:text-sm text-text-muted leading-relaxed max-w-md">
            Combina tus selecciones, explora opciones y recibe el análisis completo de John Anthony
            con cobertura real y cuotas actualizadas.
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => onSelect('mode_a')}
          className="card p-5 rounded-2xl text-left space-y-3 border-brand/40 bg-brand-soft/5 hover:border-brand/60 hover:bg-brand-soft/10 transition group"
        >
          <div className="w-11 h-11 rounded-xl bg-brand/10 border border-brand/30 flex items-center justify-center">
            <Zap className="h-5 w-5 text-brand" />
          </div>
          <div>
            <p className="text-sm font-black text-text">Créalo con John Anthony</p>
            <p className="text-[10px] text-text-dim mt-0.5">La IA construye el parlay por ti</p>
          </div>
        </button>
        <button
          onClick={() => onSelect('mode_b')}
          className="card p-5 rounded-2xl text-left space-y-3 hover:border-cyan/50 hover:bg-cyan/5 transition group"
        >
          <div className="w-11 h-11 rounded-xl bg-panel-elevated border border-border flex items-center justify-center">
            <User className="h-5 w-5 text-cyan" />
          </div>
          <div>
            <p className="text-sm font-black text-text">Enséñame opciones</p>
            <p className="text-[10px] text-text-dim mt-0.5">Elige tus propias selecciones y JA las evalúa</p>
          </div>
        </button>
      </div>
    </div>
  )
}

// ─── MODE A: "Créalo con JA" ──────────────────────────────────────────────

function ModeAConfigure({
  onGenerate, generating,
}: {
  onGenerate: (input: {
    fixtures: ParlayLabFixtureSummary[]; families: ParlayLabFamily[]; riskProfile: ParlayLabRiskProfile
    targetMin: number | null; targetMax: number | null; avoidCorrelation: boolean
  }) => void; generating: boolean
}) {
  const [query, setQuery] = useState('')
  const [selectedFixtures, setSelectedFixtures] = useState<ParlayLabFixtureSummary[]>([])
  const [selectedFamilies, setSelectedFamilies] = useState<ParlayLabFamily[]>([])
  const [riskProfile, setRiskProfile] = useState<ParlayLabRiskProfile>('balanceado')
  const [targetMin, setTargetMin] = useState('')
  const [targetMax, setTargetMax] = useState('')
  const [avoidCorrelation, setAvoidCorrelation] = useState(false)

  const { data: fixturesData, isLoading: fixturesLoading } = useParlayLabFixtureSearch(true, query)
  const fixtureIds = selectedFixtures.map((f) => f.fixture_id)
  const { data: familiesData } = useParlayLabFamilies(true, fixtureIds)

  const toggleFixture = (fixture: ParlayLabFixtureSummary) => {
    setSelectedFixtures((prev) =>
      prev.some((f) => f.fixture_id === fixture.fixture_id)
        ? prev.filter((f) => f.fixture_id !== fixture.fixture_id)
        : [...prev, fixture]
    )
  }
  const toggleFamily = (family: ParlayLabFamily) => {
    setSelectedFamilies((prev) => (prev.includes(family) ? prev.filter((f) => f !== family) : [...prev, family]))
  }

  const canGenerate = selectedFixtures.length >= 2 && selectedFamilies.length >= 1 && !generating

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <p className="eyebrow">1 · Elige al menos 2 partidos</p>
        {selectedFixtures.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedFixtures.map((f) => (
              <span key={f.fixture_id} className="inline-flex items-center gap-1 text-[10px] font-bold bg-brand-soft/30 text-brand border border-brand/30 rounded-full px-2 py-1">
                {f.home_team} vs {f.away_team}
                <button onClick={() => toggleFixture(f)} aria-label="Quitar"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-dim" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Busca un equipo o competición…"
            className="w-full bg-panel-elevated border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm text-text placeholder:text-text-dim outline-none focus:border-brand/50"
          />
        </div>
        <div className="card p-2 max-h-64 overflow-y-auto divide-y divide-border/40">
          {fixturesLoading && <p className="text-xs text-text-dim p-3">Buscando partidos…</p>}
          {!fixturesLoading && (fixturesData?.fixtures?.length ?? 0) === 0 && (
            <p className="text-xs text-text-dim p-3">Sin partidos disponibles con cobertura real ahora mismo.</p>
          )}
          {fixturesData?.fixtures?.map((f) => (
            <button
              key={f.fixture_id}
              onClick={() => toggleFixture(f)}
              className={cn(
                'data-row w-full text-left -mx-1 px-1 rounded-lg transition-colors',
                selectedFixtures.some((s) => s.fixture_id === f.fixture_id) ? 'bg-brand-soft/20' : 'hover:bg-panel-elevated/30'
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex -space-x-2 shrink-0">
                  <TeamCrest src={f.home_logo} alt={f.home_team} size={22} />
                  <TeamCrest src={f.away_logo} alt={f.away_team} size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-text truncate">{f.home_team} vs {f.away_team}</p>
                  <p className="text-[10px] text-text-dim truncate">{f.competition_name} · {kickoffLabel(f.kickoff_utc)}</p>
                </div>
              </div>
              <span className={cn('shrink-0 h-5 w-5 rounded-full border flex items-center justify-center', selectedFixtures.some((s) => s.fixture_id === f.fixture_id) ? 'bg-brand border-brand' : 'border-border')}>
                {selectedFixtures.some((s) => s.fixture_id === f.fixture_id) && <CheckCircle2 className="h-4 w-4 text-black" />}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <p className="eyebrow">2 · Familias que te interesan</p>
        {fixtureIds.length === 0 ? (
          <p className="text-xs text-text-dim">Elige partidos primero para ver qué familias tienen cobertura real.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(familiesData?.families ?? []).map((f) => (
              <button
                key={f.family}
                disabled={!f.available}
                onClick={() => toggleFamily(f.family)}
                className={cn(
                  'text-xs font-bold rounded-xl px-3 py-2.5 border transition text-left',
                  !f.available && 'opacity-35 cursor-not-allowed border-border/40 text-text-dim',
                  f.available && selectedFamilies.includes(f.family) && 'bg-brand text-black border-brand',
                  f.available && !selectedFamilies.includes(f.family) && 'bg-panel-elevated text-text border-border hover:border-brand/40'
                )}
              >
                {f.label}
                {!f.available && <span className="block text-[9px] font-normal mt-0.5">Sin cobertura real</span>}
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <p className="eyebrow">3 · Tu perfil de preferencia</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {RISK_PROFILES.map((p) => (
            <button
              key={p.value}
              onClick={() => setRiskProfile(p.value)}
              className={cn(
                'text-left rounded-xl px-3 py-2.5 border transition',
                riskProfile === p.value ? 'bg-brand-soft/20 border-brand/50' : 'bg-panel-elevated border-border hover:border-brand/30'
              )}
            >
              <p className="text-xs font-black text-text">{p.label}</p>
              <p className="text-[10px] text-text-dim mt-0.5 leading-snug">{p.hint}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <p className="eyebrow">4 · Rango de cuota objetivo (opcional)</p>
        <div className="flex items-center gap-3">
          <input
            type="number" step="0.1" min="1" placeholder="Mín."
            value={targetMin} onChange={(e) => setTargetMin(e.target.value)}
            className="w-24 bg-panel-elevated border border-border rounded-xl px-3 py-2 text-sm text-text outline-none focus:border-brand/50 figure"
          />
          <span className="text-text-dim text-xs">a</span>
          <input
            type="number" step="0.1" min="1" placeholder="Máx."
            value={targetMax} onChange={(e) => setTargetMax(e.target.value)}
            className="w-24 bg-panel-elevated border border-border rounded-xl px-3 py-2 text-sm text-text outline-none focus:border-brand/50 figure"
          />
        </div>
        <p className="text-[10px] text-text-dim leading-relaxed">
          Si no cabe dentro del rango sin sacrificar calidad, John Anthony te avisa y muestra la mejor
          construcción real disponible -- nunca fuerza piernas malas para llegar al objetivo.
        </p>
      </section>

      <section className="space-y-2">
        <p className="eyebrow">5 · Correlación</p>
        <button
          onClick={() => setAvoidCorrelation((v) => !v)}
          className={cn(
            'w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 border transition text-left',
            avoidCorrelation ? 'bg-brand-soft/20 border-brand/50' : 'bg-panel-elevated border-border hover:border-brand/30',
          )}
        >
          <div>
            <p className="text-xs font-bold text-text">Eliminar selecciones correlacionadas</p>
            <p className="text-[10px] text-text-dim mt-0.5 leading-snug">
              Prioriza combinaciones sin correlación conocida entre partidos. John Anthony nunca construye
              con correlación alta, con o sin esto activado.
            </p>
          </div>
          <span className={cn('shrink-0 h-5 w-9 rounded-full border flex items-center px-0.5 transition', avoidCorrelation ? 'bg-brand border-brand justify-end' : 'bg-panel border-border justify-start')}>
            <span className="h-3.5 w-3.5 rounded-full bg-black/80" />
          </span>
        </button>
      </section>

      <button
        disabled={!canGenerate}
        onClick={() =>
          onGenerate({
            fixtures: selectedFixtures, families: selectedFamilies, riskProfile,
            targetMin: targetMin ? Number(targetMin) : null, targetMax: targetMax ? Number(targetMax) : null,
            avoidCorrelation,
          })
        }
        className={cn('btn-brand-app w-full py-3.5 text-sm', !canGenerate && 'opacity-40 cursor-not-allowed')}
      >
        <Sparkles className="h-4 w-4" />
        {generating ? 'Construyendo…' : 'Generar parlay'}
      </button>
      {generating && (
        <p className="text-[10px] text-text-dim text-center">
          Puede tardar hasta 1-2 minutos con partidos que John Anthony no analizó recientemente.
        </p>
      )}
    </div>
  )
}

// ─── MODE B: "Créalo tú mismo" ────────────────────────────────────────────

interface ManualLeg {
  fixture_id: number
  market: string
  selection: string
  market_label: string
  selection_label: string
  odds: number | null
  home_team: string
  away_team: string
}

function ModeBFlow({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState<ModeBStep>('search')
  const [query, setQuery] = useState('')
  const [legs, setLegs] = useState<ManualLeg[]>([])
  const [selectedFixture, setSelectedFixture] = useState<ParlayLabFixtureSummary | null>(null)
  const [evalResult, setEvalResult] = useState<ReturnType<typeof useEvaluateParlayLab>['data'] | null>(null)

  const { data: fixturesData, isLoading: fixturesLoading } = useParlayLabFixtureSearch(step === 'search', query)
  const { data: picksData, isLoading: picksLoading } = useParlayLabFixturePicks(step === 'picks', selectedFixture?.fixture_id ?? null)

  const evaluateMutation = useEvaluateParlayLab()

  const addLeg = (pick: ParlayLabPick, fixture: ParlayLabFixtureSummary) => {
    setLegs((prev) => [...prev, {
      fixture_id: pick.fixture_id,
      market: pick.market,
      selection: pick.selection,
      market_label: pick.market_label,
      selection_label: pick.selection_label,
      odds: pick.odds,
      home_team: fixture.home_team,
      away_team: fixture.away_team,
    }])
  }

  const removeLeg = (index: number) => {
    setLegs((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAnalyze = () => {
    if (legs.length < 2) return
    setStep('analyzing')
    const evalLegs: EvaluateLegInput[] = legs.map((l) => ({
      fixture_id: l.fixture_id, market: l.market, selection: l.selection,
    }))
    evaluateMutation.mutate(
      { legs: evalLegs },
      {
        onSuccess: (data) => {
          setEvalResult(data)
          setStep('result')
        },
        onError: () => setStep('review'),
      }
    )
  }

  // ─── SEARCH TAB ────────────────────────────────────────────────────────────

  if (step === 'search') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="text-text-dim hover:text-text transition">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="text-sm font-black text-text">Añade selecciones a tu parlay</p>
        </div>

        <div className="flex gap-1 bg-panel-elevated rounded-xl p-1">
          <button
            className="flex-1 py-2 px-3 rounded-lg bg-brand text-black text-xs font-bold"
          >
            Buscar partidos <span className="ml-1 opacity-70">({fixturesData?.fixtures?.length ?? 0})</span>
          </button>
          <button
            onClick={() => setStep('review')}
            className={cn(
              'flex-1 py-2 px-3 rounded-lg text-xs font-bold transition',
              legs.length > 0 ? 'text-text hover:bg-panel-elevated/50' : 'text-text-dim opacity-50 cursor-not-allowed'
            )}
            disabled={legs.length === 0}
          >
            Mis selecciones <span className="ml-1 opacity-70">({legs.length})</span>
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-dim" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar equipo, competición…"
            className="w-full bg-panel-elevated border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm text-text placeholder:text-text-dim outline-none focus:border-brand/50"
          />
        </div>

        <div className="card p-2 max-h-[50vh] overflow-y-auto divide-y divide-border/40">
          {fixturesLoading && <p className="text-xs text-text-dim p-3">Buscando partidos…</p>}
          {!fixturesLoading && (fixturesData?.fixtures?.length ?? 0) === 0 && (
            <p className="text-xs text-text-dim p-3">Sin partidos disponibles con cobertura real ahora mismo.</p>
          )}
          {fixturesData?.fixtures?.map((f) => (
            <button
              key={f.fixture_id}
              onClick={() => { setSelectedFixture(f); setStep('picks') }}
              className="data-row w-full text-left -mx-1 px-1 rounded-lg hover:bg-panel-elevated/30 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex -space-x-2 shrink-0">
                  <TeamCrest src={f.home_logo} alt={f.home_team} size={22} />
                  <TeamCrest src={f.away_logo} alt={f.away_team} size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-text truncate">{f.home_team} vs {f.away_team}</p>
                  <p className="text-[10px] text-text-dim truncate">{f.competition_name} · {kickoffLabel(f.kickoff_utc)}</p>
                </div>
              </div>
              <span className="shrink-0 text-[10px] font-bold text-brand">+ Añadir</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ─── PICKS TAB (select market/selection for a fixture) ────────────────────

  if (step === 'picks' && selectedFixture) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setStep('search')} className="text-text-dim hover:text-text transition">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <p className="text-sm font-black text-text truncate">Selecciona mercados</p>
            <p className="text-[10px] text-text-dim truncate">{selectedFixture.home_team} vs {selectedFixture.away_team}</p>
          </div>
        </div>

        <div className="flex gap-1 bg-panel-elevated rounded-xl p-1">
          <button
            onClick={() => setStep('search')}
            className="flex-1 py-2 px-3 rounded-lg text-xs font-bold text-text hover:bg-panel-elevated/50 transition"
          >
            Buscar partidos
          </button>
          <button
            onClick={() => setStep('review')}
            className={cn(
              'flex-1 py-2 px-3 rounded-lg text-xs font-bold transition',
              legs.length > 0 ? 'text-text hover:bg-panel-elevated/50' : 'text-text-dim opacity-50 cursor-not-allowed'
            )}
            disabled={legs.length === 0}
          >
            Mis selecciones <span className="ml-1 opacity-70">({legs.length})</span>
          </button>
        </div>

        <div className="card p-2 max-h-[50vh] overflow-y-auto divide-y divide-border/40">
          {picksLoading && <p className="text-xs text-text-dim p-3">Cargando mercados disponibles…</p>}
          {!picksLoading && (picksData?.picks?.length ?? 0) === 0 && (
            <p className="text-xs text-text-dim p-3">Sin mercados disponibles para este partido.</p>
          )}
          {picksData?.picks?.map((pick, i) => (
            <button
              key={`${pick.fixture_id}-${pick.market}-${pick.selection}-${i}`}
              onClick={() => addLeg(pick, selectedFixture)}
              className="data-row w-full text-left -mx-1 px-1 rounded-lg hover:bg-panel-elevated/30 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-text truncate">{pick.selection_label}</p>
                <p className="text-[10px] text-text-dim truncate">{pick.market_label}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="figure text-xs font-bold text-cyan">{pick.odds != null ? `@${formatOdds(pick.odds)}` : '—'}</span>
                <span className="h-6 w-6 rounded-full bg-brand/10 flex items-center justify-center">
                  <Plus className="h-3.5 w-3.5 text-brand" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ─── REVIEW TAB ────────────────────────────────────────────────────────────

  if (step === 'review') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setStep('search')} className="text-text-dim hover:text-text transition">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="text-sm font-black text-text">Mis selecciones ({legs.length})</p>
        </div>

        <div className="flex gap-1 bg-panel-elevated rounded-xl p-1">
          <button
            onClick={() => setStep('search')}
            className="flex-1 py-2 px-3 rounded-lg text-xs font-bold text-text hover:bg-panel-elevated/50 transition"
          >
            Buscar partidos
          </button>
          <button className="flex-1 py-2 px-3 rounded-lg bg-brand text-black text-xs font-bold">
            Mis selecciones <span className="ml-1 opacity-70">({legs.length})</span>
          </button>
        </div>

        {legs.length === 0 ? (
          <div className="card p-6 rounded-2xl text-center">
            <p className="text-xs text-text-dim">Aún no has añadido ninguna selección. Ve a "Buscar partidos" y toca "+ Añadir" en un partido.</p>
          </div>
        ) : (
          <div className="card p-2 divide-y divide-border/40">
            {legs.map((leg, i) => (
              <div key={i} className="data-row">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-text truncate">{leg.selection_label}</p>
                  <p className="text-[10px] text-text-dim truncate">
                    {leg.home_team} vs {leg.away_team} · {leg.market_label}
                  </p>
                </div>
                <span className="figure text-sm font-bold text-text shrink-0">{leg.odds != null ? `@${formatOdds(leg.odds)}` : '—'}</span>
                <button onClick={() => removeLeg(i)} className="ml-2 shrink-0 text-text-dim hover:text-danger transition" aria-label="Quitar selección">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => setStep('search')}
          className="w-full text-center text-xs font-bold text-brand hover:underline py-1"
        >
          + Añadir más selecciones
        </button>

        <button
          disabled={legs.length < 2 || evaluateMutation.isPending}
          onClick={handleAnalyze}
          className={cn(
            'w-full py-3.5 rounded-xl font-black text-sm transition',
            legs.length < 2
              ? 'bg-panel-elevated text-text-dim border border-border opacity-40 cursor-not-allowed'
              : 'bg-cyan text-black hover:bg-cyan/90'
          )}
        >
          {evaluateMutation.isPending ? 'Analizando…' : 'Analizar mi parlay'}
        </button>
        {legs.length < 2 && (
          <p className="text-[10px] text-text-dim text-center">Necesitas al menos 2 selecciones para analizar.</p>
        )}
      </div>
    )
  }

  // ─── ANALYZING ─────────────────────────────────────────────────────────────

  if (step === 'analyzing') {
    return (
      <div className="space-y-4 text-center py-12">
        <div className="animate-pulse">
          <Layers className="h-8 w-8 text-brand mx-auto" />
        </div>
        <p className="text-sm font-black text-text">Analizando tu parlay…</p>
        <p className="text-xs text-text-dim">Evaluando probabilidad, correlación y calidad de cada selección.</p>
      </div>
    )
  }

  // ─── RESULT (Mode B) ──────────────────────────────────────────────────────

  if (step === 'result' && evalResult) {
    return (
      <ModeBResult
        evalResult={evalResult}
        legs={legs}
        onReset={() => { setLegs([]); setEvalResult(null); setStep('search') }}
        onBackToReview={() => setStep('review')}
      />
    )
  }

  return null
}

// ─── MODE B RESULT ─────────────────────────────────────────────────────────

function ModeBResult({
  evalResult, legs, onReset, onBackToReview,
}: {
  evalResult: NonNullable<ReturnType<typeof useEvaluateParlayLab>['data']>
  legs: ManualLeg[]
  onReset: () => void
  onBackToReview: () => void
}) {
  const isRecommended = evalResult.status === 'valid'

  return (
    <div className="space-y-5">
      <button onClick={onBackToReview} className="flex items-center gap-1 text-xs font-bold text-text-dim">
        <ChevronLeft className="h-4 w-4" /> Volver a selecciones
      </button>

      <div className={cn(
        'p-4 rounded-2xl space-y-3',
        isRecommended
          ? 'bg-emerald-950/40 border border-emerald-500/40'
          : evalResult.correlation_level === 'alta'
            ? 'bg-danger-soft/10 border border-danger/30'
            : 'bg-warn-soft/10 border border-warn/30'
      )}>
        <div className="flex items-center justify-between">
          <span className={cn('text-[10px] font-black uppercase tracking-widest', isRecommended ? 'text-emerald-400' : evalResult.correlation_level === 'alta' ? 'text-danger' : 'text-warn')}>
            {isRecommended ? 'Tu parlay tiene potencial' : evalResult.correlation_level === 'alta' ? 'Demasiada correlación' : 'Sin suficiente sustento'}
          </span>
          <span className="text-[10px] font-bold text-text-dim">{legs.length} piernas</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[9px] font-bold uppercase text-text-dim">Cuota combinada</p>
            <p className="figure text-3xl font-black text-text">@{formatOdds(evalResult.combined_odds)}</p>
          </div>
          {evalResult.joint_probability_adjusted != null && (
            <div className="text-right">
              <p className="text-[9px] font-bold uppercase text-text-dim">Probabilidad conjunta</p>
              <p className="figure text-lg font-bold text-cyan">{formatProb(evalResult.joint_probability_adjusted, 1)}</p>
            </div>
          )}
        </div>
        {evalResult.risk_score != null && (
          <p className="text-[10px] text-text-dim">
            Riesgo {evalResult.risk_score >= 55 ? 'alto' : evalResult.risk_score >= 30 ? 'medio' : 'bajo'} ·{' '}
            {correlationRiskLabel(evalResult.correlation_level)}
          </p>
        )}
      </div>

      {evalResult.failure_reason && (
        <div className="card border-danger/30 bg-danger-soft/10 p-3.5 rounded-2xl flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
          <p className="text-xs text-danger leading-relaxed">{evalResult.failure_reason}</p>
        </div>
      )}

      <section className="space-y-2">
        <p className="eyebrow">Tus selecciones</p>
        <div className="card p-2 divide-y divide-border/40">
          {evalResult.legs.map((leg, i) => (
            <div key={i} className="data-row">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-text truncate">{leg.selection_label ?? leg.selection}</p>
                <p className="text-[10px] text-text-dim truncate">{leg.home_team} vs {leg.away_team}</p>
              </div>
              <span className="figure text-sm font-bold text-text shrink-0">{formatOdds(leg.odds)}</span>
            </div>
          ))}
        </div>
      </section>

      {evalResult.explanation && (
        <>
          <section className="space-y-2">
            <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Por qué le gusta a JA</p>
            <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              <ReasonList reasons={evalResult.explanation.why} emptyText="Sin factores adicionales destacados para esta combinación." />
            </div>
          </section>
          <section className="space-y-2">
            <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Qué puede fallar</p>
            <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
              <ShieldAlert className="h-4 w-4 text-warn shrink-0 mt-0.5" />
              <ReasonList reasons={evalResult.explanation.risks} emptyText="John Anthony no identifica riesgos materiales adicionales." />
            </div>
          </section>
          <section className="space-y-2">
            <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Qué hay que vigilar</p>
            <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
              <Eye className="h-4 w-4 text-cyan shrink-0 mt-0.5" />
              <ReasonList reasons={evalResult.explanation.watch} emptyText="Sin condiciones adicionales que vigilar." />
            </div>
          </section>
        </>
      )}

      <ResponsibleNotice />
      <button onClick={onReset} className="btn-secondary-app w-full py-3">Construir otro parlay</button>
    </div>
  )
}

// ─── RESULT SHARED (Mode A / generated parlay) ────────────────────────────

export function ResultStep({ result, onReset }: { result: ParlayLabResult; onReset: () => void }) {
  const [showPlayModal, setShowPlayModal] = useState(false)
  const hasPlayableLegs = result.status === 'valid' || result.status === 'target_not_achievable'
  const fixtures = Array.from(new Map(result.legs.map((l) => [l.fixture_id, l])).values())
  const matchLabel = fixtures.map((l) => `${l.home_team} vs ${l.away_team}`).join(' + ')

  if (result.status === 'informacion_insuficiente') {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={CircleSlash}
          title="Información insuficiente"
          body={result.failure_reason ?? 'No hay datos suficientes para construir un parlay con estos partidos/familias todavía.'}
        />
        <button onClick={onReset} className="btn-secondary-app w-full py-3">Elegir otros partidos</button>
      </div>
    )
  }
  if (result.status === 'no_parlay') {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={CircleSlash}
          title="Sin parlay defendible"
          body={result.failure_reason ?? 'John Anthony no encontró una combinación real con sustento suficiente entre estos partidos.'}
        />
        <button onClick={onReset} className="btn-secondary-app w-full py-3">Ajustar preferencias</button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {result.status === 'target_not_achievable' && (
        <div className="card border-warn/30 bg-warn-soft/20 flex items-start gap-2.5 p-3.5 rounded-2xl">
          <AlertTriangle className="h-4 w-4 text-warn shrink-0 mt-0.5" />
          <p className="text-xs text-warn leading-relaxed">
            {result.target_not_achievable_reason ?? 'No se alcanzó el rango de cuota pedido -- se muestra la mejor construcción real disponible.'}
          </p>
        </div>
      )}

      <div className="bg-emerald-950/40 border border-emerald-500/40 p-4 rounded-2xl space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Parlay construido por JA</span>
          <span className="text-[10px] font-bold text-text-dim">{result.legs.length} piernas</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[9px] font-bold uppercase text-text-dim">Cuota combinada (JA)</p>
            <p className="figure text-3xl font-black text-emerald-400">@{formatOdds(result.combined_odds)}</p>
          </div>
          {result.joint_probability_adjusted != null && (
            <div className="text-right">
              <p className="text-[9px] font-bold uppercase text-text-dim">Probabilidad conjunta</p>
              <p className="figure text-lg font-bold text-cyan">{formatProb(result.joint_probability_adjusted, 1)}</p>
            </div>
          )}
        </div>
        {result.risk_score != null && (
          <p className="text-[10px] text-text-dim">
            Riesgo {result.risk_score >= 55 ? 'alto' : result.risk_score >= 30 ? 'medio' : 'bajo'} ·{' '}
            {correlationRiskLabel(result.correlation_level)}
          </p>
        )}
      </div>

      <section className="space-y-2">
        <p className="eyebrow">Piernas</p>
        <div className="card p-3 divide-y divide-border/50">
          {result.legs.map((leg, i) => <LegRow key={i} leg={leg} />)}
        </div>
      </section>

      <section className="space-y-2">
        <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Por qué le gusta a JA</p>
        <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
          <ReasonList reasons={result.explanation.why} emptyText="Sin factores adicionales destacados para esta combinación." />
        </div>
      </section>
      <section className="space-y-2">
        <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Qué puede fallar</p>
        <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
          <ShieldAlert className="h-4 w-4 text-warn shrink-0 mt-0.5" />
          <ReasonList reasons={result.explanation.risks} emptyText="John Anthony no identifica riesgos materiales adicionales." />
        </div>
      </section>
      <section className="space-y-2">
        <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Qué hay que vigilar</p>
        <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
          <Eye className="h-4 w-4 text-cyan shrink-0 mt-0.5" />
          <ReasonList reasons={result.explanation.watch} emptyText="Sin condiciones adicionales que vigilar." />
        </div>
      </section>

      {hasPlayableLegs && (
        result.my_bet ? (
          <PlayedBadge bet={result.my_bet} />
        ) : (
          <button
            onClick={() => setShowPlayModal(true)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-panel-elevated border border-brand/40 text-brand hover:bg-brand/10 text-[10px] font-black uppercase tracking-wider transition"
          >
            <HandCoins className="h-3.5 w-3.5" /> La jugué
          </button>
        )
      )}
      {showPlayModal && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          matchLabel={`Parlay Lab · ${matchLabel}`}
          selectionLabel={result.legs.map((l) => l.selection_label ?? l.selection).join(' + ')}
          referenceOdds={result.combined_odds ?? 0}
          parlayLabRunId={result.id}
        />
      )}

      <ResponsibleNotice />
      <button onClick={onReset} className="btn-secondary-app w-full py-3">Construir otro parlay</button>
    </div>
  )
}

// ─── HISTORY ───────────────────────────────────────────────────────────────

function HistoryEntryCard({ run, onOpen }: { run: ParlayLabResult; onOpen: () => void }) {
  const status = STATUS_LABELS[run.status] ?? { label: run.status, className: 'text-text-dim border-border bg-panel-elevated' }
  const fixtures = Array.from(new Map(run.legs.map((l) => [l.fixture_id, l])).values())

  return (
    <button onClick={onOpen} className="card p-3.5 rounded-2xl space-y-2 w-full text-left hover:border-exact/40 transition">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold text-text-dim">{dateLabel(run.created_at)}</span>
        <span className={cn('text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border', status.className)}>
          {status.label}
        </span>
      </div>
      <div className="space-y-0.5">
        {fixtures.map((leg, i) => (
          <p key={i} className="text-xs font-semibold text-text truncate">{leg.home_team} vs {leg.away_team}</p>
        ))}
      </div>
      <p className="text-[10px] text-text-dim">
        {run.requested_families.map((f) => FAMILY_LABELS[f] ?? f).join(' · ')} · {RISK_PROFILES.find((p) => p.value === run.risk_profile)?.label ?? run.risk_profile}
      </p>
      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] text-text-dim">{run.legs.length} pierna{run.legs.length === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-3">
          {run.combined_odds != null && (
            <span className="figure text-sm font-bold text-text">@{formatOdds(run.combined_odds)}</span>
          )}
          {run.joint_probability_adjusted != null && (
            <span className="figure text-xs font-bold text-cyan">{formatProb(run.joint_probability_adjusted, 1)}</span>
          )}
          <ChevronRight className="h-3.5 w-3.5 text-text-dim" />
        </div>
      </div>
    </button>
  )
}

function HistoryList({ onOpenRun, onBack }: { onOpenRun: (run: ParlayLabResult) => void; onBack: () => void }) {
  const [offset, setOffset] = useState(0)
  const { data, isLoading } = useParlayLabHistory(true, offset)
  const items = data?.items ?? []

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-bold text-text-dim">
        <ChevronLeft className="h-4 w-4" /> Volver
      </button>
      <p className="eyebrow">Mis Parlays</p>
      {isLoading && <p className="text-xs text-text-dim">Cargando historial…</p>}
      {!isLoading && items.length === 0 && (
        <EmptyState icon={History} title="Sin generaciones todavía" body="Cuando construyas un parlay con John Anthony, aparecerá aquí -- lo hayas jugado o no." />
      )}
      <div className="space-y-2.5">
        {items.map((run) => <HistoryEntryCard key={run.id} run={run} onOpen={() => onOpenRun(run)} />)}
      </div>
      {items.length > 0 && (
        <div className="flex items-center justify-between pt-1">
          <button disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - 20))} className="text-xs font-bold text-text-dim disabled:opacity-30">← Más recientes</button>
          <button disabled={items.length < 20} onClick={() => setOffset((o) => o + 20)} className="text-xs font-bold text-text-dim disabled:opacity-30">Más antiguas →</button>
        </div>
      )}
    </div>
  )
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────

export function ParlayLabPage() {
  const { user } = useAuth()
  const isBlack = user?.plan === 'BLACK' || user?.plan === 'ADMIN' || user?.role === 'ADMIN'
  const [view, setView] = useState<Mode>('home')
  const [result, setResult] = useState<ParlayLabResult | null>(null)
  const { data: allowance } = useParlayLabAllowance(isBlack)
  const generateMutation = useGenerateParlayLab()

  if (!isBlack) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-1.5 eyebrow text-exact">
          <Layers className="h-3.5 w-3.5" /> Parlay Lab
        </div>
        <UpgradePrompt requiredPlan="BLACK" feature="Parlay Lab personalizado" />
      </div>
    )
  }

  if (view === 'history') {
    return (
      <div className="p-4 lg:max-w-2xl lg:mx-auto">
        <HistoryList
          onBack={() => setView('home')}
          onOpenRun={(run) => { setResult(run); setView('result') }}
        />
      </div>
    )
  }

  if (view === 'result' && result) {
    return (
      <div className="p-4 lg:max-w-2xl lg:mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 eyebrow text-exact">
            <Layers className="h-3.5 w-3.5" /> Parlay Lab
          </div>
          {allowance && (
            <span className="text-[10px] font-bold text-text-dim font-mono">
              {allowance.unlimited ? 'Ilimitado' : `${allowance.remaining}/${allowance.limit} hoy`}
            </span>
          )}
        </div>
        <ResultStep result={result} onReset={() => { setResult(null); setView('home') }} />
      </div>
    )
  }

  const limitReached = allowance != null && !allowance.unlimited && (allowance.remaining ?? 0) <= 0

  return (
    <div className="p-4 space-y-5 lg:max-w-2xl lg:mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 eyebrow text-exact">
          <Layers className="h-3.5 w-3.5" /> Parlay Lab
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setView('history')} className="flex items-center gap-1 text-[10px] font-bold text-text-dim hover:text-text">
            <History className="h-3.5 w-3.5" /> Mis Parlays
          </button>
          {allowance && (
            <span className="text-[10px] font-bold text-text-dim font-mono">
              {allowance.unlimited ? 'Ilimitado' : `${allowance.remaining}/${allowance.limit} hoy`}
            </span>
          )}
        </div>
      </div>

      {view === 'home' && <HomeScreen onSelect={(mode) => setView(mode)} />}

      {view === 'mode_a' && (
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setView('home')} className="text-text-dim hover:text-text transition">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <p className="text-sm font-black text-text">JA elige tus selecciones</p>
          </div>
          {limitReached ? (
            <div className="space-y-4">
              <EmptyState
                icon={Clock}
                title="Ya usaste tus generaciones de hoy"
                body={`Vuelve a intentarlo mañana (${allowance?.resets_at ? formatKickoffTime(allowance.resets_at) : ''}).`}
              />
              <button onClick={() => setView('history')} className="btn-secondary-app w-full py-3">Revisar mis parlays</button>
            </div>
          ) : (
            <>
              <ModeAConfigure
                generating={generateMutation.isPending}
                onGenerate={({ fixtures, families, riskProfile, targetMin, targetMax, avoidCorrelation }) => {
                  generateMutation.mutate(
                    {
                      fixtureIds: fixtures.map((f) => f.fixture_id), families, riskProfile,
                      targetOddsMin: targetMin, targetOddsMax: targetMax, avoidCorrelation,
                    },
                    { onSuccess: (data) => { setResult(data); setView('result') } }
                  )
                }}
              />
              {generateMutation.isError && (
                <div className="card border-danger/30 bg-danger-soft/10 p-3.5 rounded-2xl flex items-start gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
                  <p className="text-xs text-danger leading-relaxed">{getParlayLabErrorMessage(generateMutation.error)}</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {view === 'mode_b' && (
        <ModeBFlow onBack={() => setView('home')} />
      )}
    </div>
  )
}
